import {
  Check as CheckIcon,
  HandshakeOutlined as HandshakeIcon,
} from "@mui/icons-material";
import { Box, Button, Link, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { WorkItemList, WorkItemStateEnum } from "../../../../store/types/work.types";
import ActivityLink from "../../../activity-link/ActivityLink";
import { formatLastActionComment } from "../../../../utils/workItemUtils";
import StatusMark, { MarkStatus } from "./StatusMark";
import {
  AMBER,
  BOOKED_FG,
  GREEN,
  GREEN_BG,
  INK,
  INK_3,
  LINE_2,
  PAPER,
  ROSE,
  formatDateTime,
  isBookedWithCustomer,
  isOverdueItem,
} from "./types";

interface SubItemProps {
  item: WorkItemList;
  customerNo: number;
  onComplete?: (item: WorkItemList) => void;
}

const COMMENT_CLAMP_LIMIT = 160;

const SubItem = ({ item, customerNo, onComplete }: SubItemProps) => {
  const [commentExpanded, setCommentExpanded] = useState(false);
  const done = item.State === WorkItemStateEnum.Closed;
  const overdue = isOverdueItem(item);
  const active = item.State === WorkItemStateEnum.Active;
  const noDate = active && !item.ScheduledTo;
  const booked = isBookedWithCustomer(item.Type);
  const comment = done ? formatLastActionComment(item.Result) : null;
  const commentTooLong = !!comment && comment.length > COMMENT_CLAMP_LIMIT;

  const markStatus: MarkStatus = overdue ? "late" : done ? "done" : "todo";
  const isMultiline = !!comment || !!item.Description;

  return (
    <ActivityLink customerNo={customerNo} workItemNo={item.Id}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          gap: 1.25,
          alignItems: isMultiline ? "flex-start" : "center",
          p: "5px 6px",
          borderRadius: "8px",
          "&:hover": { bgcolor: "rgba(0,0,0,.03)" },
          "@media (hover: hover)": {
            "& .row-complete": { opacity: 0 },
            "&:hover .row-complete": { opacity: 1 },
          },
        }}
      >
        <Box sx={{ mt: isMultiline ? "2px" : 0 }}>
          <StatusMark type="activity" status={markStatus} size={18} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: done ? 400 : 600,
              color: done ? INK_3 : INK,
              lineHeight: 1.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.Name}
          </Typography>
          {item.Description && (
            <Typography
              sx={{
                fontSize: 12,
                color: INK_3,
                lineHeight: 1.35,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.Description}
            </Typography>
          )}
          {comment && (
            <>
              <Typography
                sx={{
                  fontSize: 12,
                  color: INK_3,
                  fontStyle: "italic",
                  mt: 0.25,
                  lineHeight: 1.4,
                  ...(commentTooLong && !commentExpanded
                    ? {
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }
                    : {}),
                }}
              >
                "{comment}"
              </Typography>
              {commentTooLong && (
                <Link
                  component="button"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: INK_3,
                    mt: 0.25,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCommentExpanded((v) => !v);
                  }}
                >
                  {commentExpanded ? "Visa mindre" : "Visa mer"}
                </Link>
              )}
            </>
          )}
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ whiteSpace: "nowrap", mt: isMultiline ? "2px" : 0 }}
        >
          {active && onComplete && (
            <Button
              className="row-complete"
              size="small"
              variant="outlined"
              startIcon={<CheckIcon sx={{ fontSize: 12 }} />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onComplete(item);
              }}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 11,
                lineHeight: 1.2,
                px: 0.85,
                py: 0.15,
                minHeight: 0,
                color: GREEN,
                borderColor: LINE_2,
                bgcolor: PAPER,
                transition: "opacity .12s ease",
                "& .MuiButton-startIcon": { mr: 0.4 },
                "&:hover": {
                  borderColor: GREEN,
                  bgcolor: GREEN_BG,
                  color: GREEN,
                },
              }}
            >
              Avsluta
            </Button>
          )}
          {booked && (
            <Tooltip title="Avtalad tid med kunden" arrow>
              <HandshakeIcon sx={{ fontSize: 15, color: BOOKED_FG }} />
            </Tooltip>
          )}
          <Typography
            sx={{
              fontSize: 11.5,
              color: overdue ? ROSE : noDate ? AMBER : INK_3,
              fontWeight: overdue || noDate ? 600 : 400,
            }}
          >
            {noDate ? "Utan datum" : formatDateTime(item.ScheduledTo)}
          </Typography>
        </Stack>
      </Box>
    </ActivityLink>
  );
};

export default SubItem;
